/**
 * Copyright (c) 2025 George Nassef, Vector Engineering LLC
 * All rights reserved.
 * 
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 * Commercial use requires explicit written permission.
 */

import React from 'react';
import { 
  Container,
  Typography, 
  Button, 
  Box,
  Stack,
  Card,
  CardContent,
  CardMedia,
  Grid
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import CreateIcon from '@mui/icons-material/Create';

const HomePage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      {/* Hero Section */}
      <Box 
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          mb: 10
        }}
      >
        <Typography 
          variant="h2" 
          component="h1" 
          gutterBottom
          className="teen-heading"
          sx={{ 
            fontSize: { xs: '2.5rem', md: '3.5rem' },
            mb: 3
          }}
        >
          DramazNow
        </Typography>
        
        <Typography 
          variant="h5" 
          color="text.secondary"
          sx={{ 
            maxWidth: 650,
            mb: 6,
            lineHeight: 1.6
          }}
        >
          Create compelling animated dramas with just a few clicks using our AI-powered script generation
        </Typography>
        
        <Button
          data-testid="create-script-button"
          variant="contained"
          color="primary"
          size="large"
          startIcon={<CreateIcon />}
          onClick={() => navigate('/create-script')}
          className="teen-button-primary"
          sx={{ 
            py: 1.5,
            px: 4,
            fontSize: '1.1rem'
          }}
        >
          Start Creating
        </Button>
      </Box>

      {/* Main Features */}
      <Box sx={{ mb: 12 }}>
        <Grid container spacing={6}>
          <Grid item xs={12} md={4}>
            <Card 
              elevation={2} 
              sx={{ 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                bgcolor: 'background.paper',
                borderRadius: 2,
                transition: 'all 0.3s',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: 8
                }
              }}
            >
              <CardContent sx={{ flexGrow: 1, p: 4 }}>
                <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 2, fontWeight: 'bold' }}>
                  Smart Script Generation
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph>
                  Our AI crafts realistic dialogue and compelling storylines based on your 
                  chosen scenarios, ensuring engaging content that resonates with diverse audiences.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Card 
              elevation={2} 
              sx={{ 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                bgcolor: 'background.paper',
                borderRadius: 2,
                transition: 'all 0.3s',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: 8
                }
              }}
            >
              <CardContent sx={{ flexGrow: 1, p: 4 }}>
                <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 2, fontWeight: 'bold' }}>
                  Realistic Character Animation
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph>
                  Choose from a diverse library of detailed 3D character models with customizable 
                  voices and expressions that bring your scripts to life in authentic settings.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Card 
              elevation={2} 
              sx={{ 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                bgcolor: 'background.paper',
                borderRadius: 2,
                transition: 'all 0.3s',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: 8
                }
              }}
            >
              <CardContent sx={{ flexGrow: 1, p: 4 }}>
                <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 2, fontWeight: 'bold' }}>
                  One-Click Video Export
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph>
                  Transform your scripts into fully-rendered videos with automatic lip-syncing, 
                  camera movements, and scene transitions ready to share on any platform.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Spacer */}
      <Box sx={{ height: 60 }} />
    </Container>
  );
};

export default HomePage;
